import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { Users, Target, Award } from 'lucide-react';
interface PilotSlide1Props {
  isDark?: boolean;
}
export function PilotSlide1({
  isDark = false
}: PilotSlide1Props) {
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.12,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.25, 0.1, 0.25, 1]
      }
    }
  };
  return <motion.div className="h-full w-full overflow-y-auto overflow-x-hidden" variants={containerVariants} initial="hidden" animate="visible" data-id="element-461">
      <div className="min-h-full flex flex-col justify-start md:justify-center max-w-6xl mx-auto px-6 sm:px-12 py-20 sm:py-24" data-id="element-462">
        <motion.div variants={itemVariants} className="mb-10 sm:mb-12 text-center" data-id="element-463">
          <h1 className={`text-3xl sm:text-5xl font-serif font-medium mb-4 sm:mb-5 tracking-tight ${isDark ? 'text-white' : 'text-heliona-charcoal'}`} data-id="element-464">
            Who We Are
          </h1>
          <p className={`text-base sm:text-xl max-w-3xl mx-auto leading-relaxed ${isDark ? 'text-white/80' : 'text-heliona-text-gray'}`} data-id="element-465">
            Heliona is a leadership risk reduction system designed to help
            people leaders identify strain early, build healthier leadership
            habits, and reduce burnout before it turns into turnover.
          </p>
        </motion.div>

        <motion.div variants={itemVariants} className="mb-12 sm:mb-16" data-id="element-466">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8 max-w-5xl mx-auto" data-id="element-467">
            {[{
            name: 'Ebrima A. Sisay',
            title: 'Founder & Chief Empathy Officer',
            image: 'https://cdn.magicpatterns.com/uploads/ggwavy6HEhSdAGirbPC9EV/Ebrima_Abraham_Sisay.png'
          }, {
            name: 'Dr. Katie Gaebel',
            title: 'Chief Operating Officer',
            image: 'https://cdn.magicpatterns.com/uploads/a9G7BAyJCCoxe6nbRhxJLz/Dr._Katie_Gaebel.png'
          }, {
            name: 'Cosmin Artene',
            title: 'Lead Developer & Empathy Deployer',
            image: 'https://cdn.magicpatterns.com/uploads/7hP7aEkQVDiR6iLMKZ4TkU/Cosmin_Artene.png'
          }, {
            name: 'Bethriz D. Roa',
            title: 'Account Manager & Smile Facilitator',
            image: 'https://cdn.magicpatterns.com/uploads/hPqbAku9SyevWAARTAD74e/Bethriz_Donita_Roa.png'
          }].map((person, idx) => <div key={idx} className="text-center group" data-id="element-468">
                <div className="relative mb-3 sm:mb-4" data-id="element-469">
                  <div className="absolute inset-0 bg-heliona-mint/10 rounded-full blur-xl group-hover:bg-heliona-mint/20 transition-colors duration-300" data-id="element-470"></div>
                  <img src={person.image} alt={person.name} className={`relative w-20 h-20 sm:w-24 sm:h-24 rounded-full object-cover mx-auto border-2 shadow-lg ${isDark ? 'border-white/20' : 'border-heliona-beige'}`} data-id="element-471" />
                </div>
                <h3 className={`font-semibold text-sm sm:text-base mb-1 ${isDark ? 'text-white' : 'text-heliona-charcoal'}`} data-id="element-472">
                  {person.name}
                </h3>
                <p className={`text-xs sm:text-sm leading-tight ${isDark ? 'text-white/70' : 'text-heliona-text-gray'}`} data-id="element-473">
                  {person.title}
                </p>
              </div>)}
          </div>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 mb-10 sm:mb-12" data-id="element-474">
          <motion.div variants={itemVariants} className={`rounded-3xl p-6 sm:p-8 text-center shadow-sm hover:shadow-md transition-shadow duration-300 ${isDark ? 'bg-white/[0.03] border border-white/10' : 'bg-white border border-heliona-beige'}`} data-id="element-475">
            <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-heliona-mint/10 flex items-center justify-center mb-4 sm:mb-5 mx-auto border border-heliona-mint/20" data-id="element-476">
              <Target className="text-heliona-mint-dark w-7 h-7 sm:w-8 sm:h-8" data-id="element-477" />
            </div>
            <h3 className={`text-sm sm:text-base font-bold mb-2 sm:mb-3 ${isDark ? 'text-white' : 'text-heliona-charcoal'}`} data-id="element-478">
              Our Mission
            </h3>
            <p className={`text-xs sm:text-sm leading-relaxed ${isDark ? 'text-white/70' : 'text-heliona-text-gray'}`} data-id="element-479">
              Prevent leadership burnout before it impacts your organization's
              performance and culture
            </p>
          </motion.div>

          <motion.div variants={itemVariants} className={`rounded-3xl p-6 sm:p-8 text-center shadow-sm hover:shadow-md transition-shadow duration-300 ${isDark ? 'bg-white/[0.03] border border-white/10' : 'bg-white border border-heliona-beige'}`} data-id="element-480">
            <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-heliona-mint/10 flex items-center justify-center mb-4 sm:mb-5 mx-auto border border-heliona-mint/20" data-id="element-481">
              <Users className="text-heliona-mint-dark w-7 h-7 sm:w-8 sm:h-8" data-id="element-482" />
            </div>
            <h3 className={`text-sm sm:text-base font-bold mb-2 sm:mb-3 ${isDark ? 'text-white' : 'text-heliona-charcoal'}`} data-id="element-483">
              Our Approach
            </h3>
            <p className={`text-xs sm:text-sm leading-relaxed ${isDark ? 'text-white/70' : 'text-heliona-text-gray'}`} data-id="element-484">
              Continuous insight, targeted coaching, and personalized
              development for people leaders
            </p>
          </motion.div>

          <motion.div variants={itemVariants} className={`rounded-3xl p-6 sm:p-8 text-center shadow-sm hover:shadow-md transition-shadow duration-300 ${isDark ? 'bg-white/[0.03] border border-white/10' : 'bg-white border border-heliona-beige'}`} data-id="element-485">
            <div className={`w-14 h-14 sm:w-16 sm:h-16 rounded-2xl flex items-center justify-center mb-4 sm:mb-5 mx-auto border ${isDark ? 'bg-white/10 border-white/20' : 'bg-heliona-charcoal/10 border-heliona-charcoal/20'}`} data-id="element-486">
              <Award className={`w-7 h-7 sm:w-8 sm:h-8 ${isDark ? 'text-white' : 'text-heliona-charcoal'}`} data-id="element-487" />
            </div>
            <h3 className={`text-sm sm:text-base font-bold mb-2 sm:mb-3 ${isDark ? 'text-white' : 'text-heliona-charcoal'}`} data-id="element-488">
              Our Promise
            </h3>
            <p className={`text-xs sm:text-sm leading-relaxed ${isDark ? 'text-white/70' : 'text-heliona-text-gray'}`} data-id="element-489">
              Measurable ROI through improved leadership capacity, clarity, and
              resilience
            </p>
          </motion.div>
        </div>

        <motion.div variants={itemVariants} className={`rounded-3xl p-6 sm:p-8 max-w-4xl mx-auto mb-16 sm:mb-0 shadow-sm ${isDark ? 'bg-heliona-mint/10 border border-heliona-mint/30' : 'bg-heliona-pastel-mint border border-heliona-mint/30'}`} data-id="element-490">
          <p className={`text-base sm:text-xl font-medium text-center leading-relaxed ${isDark ? 'text-white' : 'text-heliona-charcoal'}`} data-id="element-491">
            We help organizations protect their most valuable asset:{' '}
            <span className={`font-semibold italic ${isDark ? 'text-heliona-mint' : 'text-heliona-mint-dark'}`} data-id="element-492">
              leadership capacity
            </span>
          </p>
        </motion.div>
      </div>
    </motion.div>;
}